import * as Sentry from '@sentry/browser';
import {
  textValidator, phoneNumberValidator, emailValidator, containsForbiddenWords,
  passwordValidator,
} from './input-validators';
import getSessionInfo from './session-Info';
import {
  getFullName, getQueryParam, getSanitizedErrorMessage, getUniqueUserIdFromQueryParam,
  getUserIdentifierForAnalytics,
  redirectToErrorPage,
} from './common-helper';
import getSelectedCountryInfo from '../country-code/country-code-helper';
import {
  isAdaptiveuBrand,
  isAnytimeBrand,
  isChatSupportBrand,
  isOnboardingRequired,
  isSetmoreBrand,
  isLexReceptionUKBrand,
  isSetmoreStartNowPage,
  isEmailValidityCheckEnabledBrand,
} from './brands';
import {
  ANYTIME_CONTACT_FIELDS, COMMON_CONTACT_FIELDS, CHATSUPPORT_CONTACT_FIELDS,
  SETMORE_CONTACT_FIELDS, LEXUK_CONTACT_FIELDS,
  SETMORE_START_NOW_PAGE_CONTACT_FIELDS,
  FORM_ERROR_MESSAGES,
  EMAIL_API_STATUS,
  SIGNUP_TYPES,
  EMAIL_AVAILABILITY_STATUS,
} from '../utils/common-literals';
import { updateContact, updateLoader, updateRecaptcha } from '../store/dispatch-actions';
import {
  getReCaptchaTokenAndExecuteCallback,
  initializeCheckboxRecaptcha,
  RECAPTCHA_ACTIONS,
  RECAPTCHA_TYPE,
} from './recaptcha';
import { makeRequestToCheckEmailAvailability, sendSignupEvent } from './fetch-helper';
import { analyticsSignupTypeMap, trackFailure } from './analytics';

const { leadInfo = {} } = getSessionInfo() || {};
const { iso2: countryCode, code: country } = getSelectedCountryInfo(leadInfo.countryCode);

export function isSocialLoginAuthorizedEmail(value) {
  const { email } = leadInfo;
  return email === value;
}

export function doesEmailContainRestrictedDomain(validEmailId) {
  return validEmailId?.toLowerCase()?.endsWith('@anywhere.co');
}

export function isEmailValid(value) {
  const { authType, authorized } = leadInfo;
  if (authType !== 'REGULAR') {
    return isSocialLoginAuthorizedEmail(value) ? !!authorized : emailValidator(value);
  }
  return emailValidator(value);
}

export function getContactFields() {
  if (isAnytimeBrand) {
    return ANYTIME_CONTACT_FIELDS;
  }
  if (isChatSupportBrand) {
    return CHATSUPPORT_CONTACT_FIELDS;
  }
  if (isSetmoreBrand) {
    return isSetmoreStartNowPage()
      ? SETMORE_START_NOW_PAGE_CONTACT_FIELDS
      : SETMORE_CONTACT_FIELDS;
  }
  if (isLexReceptionUKBrand) {
    return LEXUK_CONTACT_FIELDS;
  }
  return COMMON_CONTACT_FIELDS;
}

export function getDecodedContactParam() {
  try {
    const contactParam = getQueryParam('contact');
    return contactParam ? JSON.parse(atob(decodeURIComponent(contactParam))) : {};
  } catch (e) {
    const err = new Error(`Exception decoding contact param : ${e?.message}`);
    err.name = 'ContactParamDecodeError';
    err.cause = e;
    Sentry.captureException(err, {
      extra: {
        params: window?.location?.search,
      },
    });
  }
  return {};
}

export function getDirectSignupInitialState() {
  const contactFields = getContactFields();
  return {
    fullName: {
      value: '',
      valid: undefined,
      errorMsg: contactFields.fullName?.errorMsg,
    },
    companyName: {
      value: '',
      valid: undefined,
      errorMsg: contactFields.companyName?.errorMsg,
    },
    phoneNumber: {
      nationalNumber: '',
      countryCode,
      country,
      valid: undefined,
      errorMsg: contactFields.phoneNumber?.errorMsg,
    },
    email: {
      value: '',
      valid: undefined,
      errorMsg: contactFields.email?.errorMsg,
      availabilityCheckApiStatus: EMAIL_API_STATUS.NOT_INPROGRESS,
      lookupCache: [],
    },
    password: {
      value: '',
      valid: undefined,
      errorMsg: contactFields.password?.errorMsg,
    },
  };
}

export function getLeadInitialState() {
  const contactFields = getContactFields();
  const {
    firstName, lastName, companyName, phone, email,
  } = leadInfo;
  const fullName = getFullName({ firstName, lastName });
  return {
    fullName: {
      value: fullName,
      valid: (fullName && textValidator(fullName)) || undefined,
      errorMsg: contactFields.fullName?.errorMsg,
    },
    companyName: {
      value: companyName,
      valid: (companyName && textValidator(companyName)) || undefined,
      errorMsg: contactFields.companyName?.errorMsg,
    },
    phoneNumber: {
      nationalNumber: phone,
      countryCode,
      country,
      valid: (phone && phoneNumberValidator(phone, countryCode)) || undefined,
      errorMsg: contactFields.phoneNumber?.errorMsg,
    },
    email: {
      value: email,
      valid: email ? isEmailValid(email) : undefined,
      availabilityCheckApiStatus: EMAIL_API_STATUS.NOT_INPROGRESS,
      lookupCache: [],
      disabled: false,
      errorMsg: contactFields.email?.errorMsg,
    },
    password: {
      value: '',
      valid: undefined,
      errorMsg: contactFields.password?.errorMsg,
    },
  };
}

export function getSocialLoginInitialState() {
  const {
    firstName, lastName, companyName, phone, email, authorized,
  } = leadInfo;
  const fullName = getFullName({ firstName, lastName });

  return {
    fullName: { value: fullName, valid: (fullName && textValidator(fullName)) || undefined },
    companyName: {
      value: companyName,
      valid: (companyName && textValidator(companyName)) || undefined,
    },
    phoneNumber: {
      nationalNumber: phone,
      countryCode,
      country,
      valid: (phone && phoneNumberValidator(phone, countryCode)) || undefined,
    },
    email: {
      value: email,
      valid: !!authorized,
      disabled: !!authorized,
      lookupCache: [
        {
          email,
          status: authorized
            ? EMAIL_AVAILABILITY_STATUS.AVAILABLE
            : EMAIL_AVAILABILITY_STATUS.UNAVAILABLE,
        },
      ],
    },
    password: {
      value: null,
      valid: authorized || undefined,
      disabled: !!authorized,
    },
  };
}

export function configureStatePerBrand(initialState) {
  let modifiedState = initialState;
  if (isAdaptiveuBrand) {
    modifiedState = {
      ...initialState,
      phoneNumber: {
        ...initialState.phoneNumber,
        countryCode: null,
        valid: true,
        disabled: true,
      },
    };
  } else if (isOnboardingRequired) {
    modifiedState = {
      ...initialState,
      password: { value: null, valid: true, disabled: true },
    };
  } else if (isAnytimeBrand) {
    const contactObj = getDecodedContactParam();
    modifiedState = {
      ...initialState,
      ...(contactObj?.email && { email: { ...(initialState.email), value: contactObj.email } }),
      phoneNumber: { ...initialState.phoneNumber, valid: true, isOptional: true },
    };
  } else if (isChatSupportBrand) {
    modifiedState = {
      ...initialState,
      fullName: { value: null, valid: true, disabled: true },
      companyName: { value: null, valid: true, disabled: true },
      phoneNumber: { ...initialState.phoneNumber, valid: true, isOptional: true },
    };
  } else if (isSetmoreBrand) {
    const contactObj = getDecodedContactParam();
    modifiedState = {
      ...initialState,
      ...(contactObj?.email && { email: { ...(initialState.email), value: contactObj.email } }),
      companyName: { value: null, valid: true, disabled: true },
      phoneNumber: {
        ...initialState.phoneNumber,
        countryCode: null,
        value: null,
        valid: true,
        disabled: true,
      },
    };
  }

  return modifiedState;
}

export default function getContactInitialState() {
  let initialState;

  if (leadInfo?.email && leadInfo?.authType !== 'REGULAR') {
    initialState = getSocialLoginInitialState();
  } else if (leadInfo.id) {
    initialState = getLeadInitialState();
  } else {
    initialState = getDirectSignupInitialState();
  }

  return configureStatePerBrand(initialState);
}

export const validateFullName = (field, value) => {
  const fields = getContactFields();
  let { errorMsg } = fields[field];

  let isValid = textValidator(value);
  if (isValid && isSetmoreStartNowPage() && containsForbiddenWords(value)) {
    isValid = false;
    errorMsg = FORM_ERROR_MESSAGES.fullName.containsForBiddenWords;
  }

  return { isValid, errorMsg };
};

export const validateEmailId = (field, value) => {
  const fields = getContactFields();
  let { errorMsg } = fields[field];

  let isValid = emailValidator(value);

  if (isValid && doesEmailContainRestrictedDomain(value)) {
    isValid = false;
    errorMsg = FORM_ERROR_MESSAGES.email.alreadyExists;
  }

  if (isValid && isSetmoreStartNowPage() && containsForbiddenWords(value)) {
    isValid = false;
    errorMsg = FORM_ERROR_MESSAGES.email.containsForBiddenWords;
  }

  return { isValid, errorMsg };
};

export const validateAutofillFields = (state, dispatch) => {
  const email = state.contact.email.value;
  const password = state.contact.password.value;

  if (!email || !validateEmailId('email', email).isValid) {
    dispatch(updateContact({ field: 'email', value: { ...state.contact.email, valid: false } }));
    return false;
  }

  if (!password || !passwordValidator(password)) {
    dispatch(updateContact({ field: 'password', value: { ...state.contact.password, valid: false } }));
    return false;
  }

  return true;
};

function checkEmailAvailabilitySuccessCallback({
  response,
  contact,
  dispatch,
  recaptcha,
  signupType,
  value,
}) {
  if (response.data?.isEmailAvailable) {
    dispatch(
      updateContact({
        field: 'email',
        value: {
          ...contact.email,
          value,
          valid: true,
          availabilityCheckApiStatus: EMAIL_API_STATUS.COMPLETED,
          lookupCache: [
            ...contact.email.lookupCache,
            { email: value, status: EMAIL_AVAILABILITY_STATUS.AVAILABLE },
          ],
        },
      }),
    );
  } else {
    dispatch(
      updateContact({
        field: 'email',
        value: {
          ...contact.email,
          value,
          valid: false,
          errorMsg: FORM_ERROR_MESSAGES.email.alreadyExists,
          availabilityCheckApiStatus: EMAIL_API_STATUS.COMPLETED,
          lookupCache: [
            ...(contact?.email?.lookupCache || []),
            { email: value, status: EMAIL_AVAILABILITY_STATUS.UNAVAILABLE },
          ],
        },
      }),
    );
    if (isEmailValidityCheckEnabledBrand) {
      dispatch(updateLoader({ show: false }));
    }
  }
  if (
    response.message?.startsWith('Recaptcha')
    && !recaptcha?.invisibleCaptchaFailed
  ) {
    dispatch(
      updateRecaptcha({
        data: {
          checkBoxChecked: undefined,
          display: [
            SIGNUP_TYPES.NO_CARD_SIGNUP,
            SIGNUP_TYPES.FREE_SIGNUP,
          ].includes(signupType),
          invisibleCaptchaFailed: true,
        },
      }),
    );
    if (
      [SIGNUP_TYPES.NO_CARD_SIGNUP, SIGNUP_TYPES.FREE_SIGNUP].includes(
        signupType,
      )
    ) {
      initializeCheckboxRecaptcha(
        signupType === SIGNUP_TYPES.NO_CARD_SIGNUP
          ? RECAPTCHA_ACTIONS.NOCARD_SIGNUP
          : RECAPTCHA_ACTIONS.FREE_SIGNUP,
      );
      dispatch(updateLoader({ show: false }));
    }
    sendSignupEvent({
      signupType: analyticsSignupTypeMap[signupType],
      status: 'Intermediate',
      rVersion: RECAPTCHA_TYPE.INVISIBLE,
      email: value,
      accountId: getUniqueUserIdFromQueryParam(),
      failureReason: getSanitizedErrorMessage(response).analyticsMessage,
      comments: 'API call - checkEmailExists',
    });
  }
}

function checkEmailAvailabilityFailureCallback({
  response,
  dispatch,
  signupType,
  value,
}) {
  dispatch(updateLoader({ show: false }));
  if (response?.message) {
    trackFailure({
      type: analyticsSignupTypeMap[signupType],
      collatedUID: getUserIdentifierForAnalytics({ email: value }),
      email: value,
      comments: 'API call - checkEmailExists',
      message: getSanitizedErrorMessage(response).analyticsMessage,
    });
  }
  redirectToErrorPage();
}

export function checkEmailAvailability(params = {}) {
  if (params.contact?.email?.availabilityCheckApiStatus === EMAIL_API_STATUS.INPROGRESS) {
    return;
  }

  if (isEmailValidityCheckEnabledBrand) {
    params.dispatch(
      updateContact({
        field: 'email',
        value: {
          ...params.contact?.email,
          valid: true,
          availabilityCheckApiStatus: EMAIL_API_STATUS.INPROGRESS,
        },
      }),
    );
  }

  getReCaptchaTokenAndExecuteCallback(
    RECAPTCHA_ACTIONS.EMAIL_EXISTS_CHECK,
    captchaToken => makeRequestToCheckEmailAvailability({
      email: params.value,
      captchaToken,
      successCallBack:
        (response = {}) => checkEmailAvailabilitySuccessCallback({ ...params, response }),
      failureCallBack:
        (response = {}) => checkEmailAvailabilityFailureCallback({ ...params, response }),
    }),
  );
}
const fields = getContactFields();
const fieldValidators = {
  fullName: validateFullName,
  companyName: validateFullName,
  phoneNumber: (field, value) => ({
    isValid: phoneNumberValidator(value.nationalNumber, value.countryCode),
    errorMsg: fields.phoneNumber?.errorMsg,
  }),
  email: (field, value) => {
    if (value?.alreadyExists) {
      return {
        isValid: false,
        errorMsg: FORM_ERROR_MESSAGES.email.alreadyExists,
      };
    }
    return validateEmailId(field, value.email);
  },
  password: (field, value) => ({
    isValid: passwordValidator(value),
    errorMsg: fields.password?.errorMsg,
  }),
};

export const validateContactFields = (contact, dispatch) => {
  let updatedContact = { ...contact };

  Object.keys(fieldValidators)
    .filter(field => !contact[field]?.disabled)
    .forEach((field) => {
      let value = contact[field]?.value;
      if (field === 'phoneNumber') {
        value = {
          nationalNumber: contact[field]?.nationalNumber,
          countryCode: contact[field]?.countryCode,
        };
      } else if (field === 'email') {
        value = {
          email: contact[field]?.value,
          alreadyExists:
            contact.email.lookupCache?.findIndex(
              lookup => lookup.email === contact.email.value
                && lookup.status === EMAIL_AVAILABILITY_STATUS.UNAVAILABLE,
            ) !== -1,
        };
      }

      const { isValid, errorMsg } = fieldValidators[field](field, value);

      let valid;
      if (field === 'phoneNumber') {
        valid = isValid || !!(contact[field]?.isOptional && !contact[field]?.nationalNumber);
      } else {
        valid = isValid || !!(contact[field]?.isOptional && !contact[field]?.value);
      }

      updatedContact = {
        ...updatedContact,
        [field]: { ...contact[field], valid, errorMsg },
      };
      dispatch(
        updateContact({ field, value: { ...contact[field], valid, errorMsg } }),
      );
    });

  return updatedContact;
};
